/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { SpendTreeItem, Row } from "./types";

export function getSumOfCosts(items: SpendTreeItem[]) {
  return items.reduce((acc, curr) => acc + curr.cost, 0);
}

export function getSumOfBudgets(items: SpendTreeItem[]) {
  return items.reduce((acc, curr) => acc + (curr.monthlyBudget ?? 0), 0);
}

export function getSumOfPreviousCosts(items: SpendTreeItem[]) {
  return items.reduce((acc, curr) => acc + curr.previousCost, 0);
}

export function getSumOfForecasts(items: SpendTreeItem[]) {
  const sum = items.reduce((acc, curr) => acc + curr.forecast, 0);

  // Check definition of `formatMoneyWithFallback` function for reasoning behind this
  if (sum < 0) {
    return "N/A";
  }

  return sum;
}

type ValueField = "cost" | "forecast" | "monthlyBudget" | "prevCost";

export function getCostFieldValue(
  field: ValueField,
  { original: values, isExpanded }: Row
) {
  const result = getValuesByType(field, values);

  return isExpanded ? result?.expanded : result?.collapsed;
}

function getValuesByType(id: ValueField, values: SpendTreeItem) {
  switch (id) {
    case "cost":
      return { expanded: values.parentCost, collapsed: values.cost };
    case "prevCost":
      return {
        expanded: values.parentPreviousCost,
        collapsed: values.previousCost,
      };
    case "forecast":
      return { expanded: values.parentForecast, collapsed: values.forecast };
    case "monthlyBudget":
      // We want to show the same value here as parent and children budgets are independent.
      return {
        expanded: values.monthlyBudget,
        collapsed: values.monthlyBudget,
      };
    default:
      throw new Error("Unsupported id.");
  }
}
